import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Printer, ShieldCheck, ShieldOff, Calendar, Clock, Pencil, Lock, Mail, MailPlus } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { v: "admin", l: "Admin" },
  { v: "agente", l: "Agente" },
  { v: "backoffice", l: "Back-office" },
  { v: "collaboratore", l: "Collaboratore" },
];
const empty = { email: "", password: "", name: "", role: "collaboratore" };

/** Pallino di presenza accanto al nome utente. Verde = online (≤5min), giallo (≤30min), grigio = offline. */
function PresenceDot({ lastSeenAt }) {
  if (!lastSeenAt) {
    return <span className="inline-block h-2 w-2 rounded-full bg-slate-300" title="Mai visto" />;
  }
  const diffMin = (Date.now() - new Date(lastSeenAt).getTime()) / 60000;
  let cls = "bg-slate-300";
  let label = "Offline";
  if (diffMin <= 5) { cls = "bg-emerald-500"; label = "Online ora"; }
  else if (diffMin <= 30) { cls = "bg-amber-400"; label = `Visto ${Math.round(diffMin)} min fa`; }
  else if (diffMin <= 60 * 24) { cls = "bg-slate-400"; label = `Visto ${Math.round(diffMin / 60)}h fa`; }
  else { label = `Visto ${Math.round(diffMin / (60 * 24))}gg fa`; }
  return <span className={`inline-block h-2 w-2 rounded-full ${cls}`} title={label} />;
}

const ROLE_COLOR = {
  admin: "bg-purple-50 text-purple-700 border-purple-200",
  agente: "bg-blue-50 text-blue-700 border-blue-200",
  backoffice: "bg-slate-100 text-slate-700 border-slate-200",
  collaboratore: "bg-amber-50 text-amber-700 border-amber-200",
  broker: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cliente: "bg-sky-50 text-sky-700 border-sky-200",
};

const PERMISSION_SECTIONS = [
  { k: "clients", l: "Clienti" }, { k: "policies", l: "Polizze" }, { k: "quotes", l: "Preventivi" },
  { k: "commissions", l: "Provvigioni" }, { k: "companies", l: "Compagnie" }, { k: "brokers", l: "Broker" },
  { k: "agency", l: "Anagrafica Agenzia" }, { k: "categories", l: "Categorie" },
];
const LEVEL_LABEL = { none: "Nessuno", read: "Lettura", rw: "Lettura + Scrittura" };

const TABS = [
  { v: "staff", l: "Staff CRM", desc: "Admin, agenti, back-office, collaboratori" },
  { v: "cliente", l: "Portali Clienti", desc: "Account portale cliente (read-only)" },
  { v: "broker", l: "Portali Broker", desc: "Account portale broker (read-only)" },
];

const formatExpiry = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  } catch { return iso; }
};

const isExpired = (iso) => {
  if (!iso) return false;
  try { return new Date(iso).getTime() < Date.now(); } catch { return false; }
};

const StatusCell = ({ u, expired, active }) => {
  if (u.role === "admin") {
    return <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Sempre attivo</span>;
  }
  if (active) {
    return <span data-testid={`status-active-${u.id}`} className="inline-flex items-center gap-1.5 text-xs text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> Attivo</span>;
  }
  if (expired) {
    return <span data-testid={`status-expired-${u.id}`} className="inline-flex items-center gap-1.5 text-xs text-red-700"><Clock className="h-3.5 w-3.5" /> Scaduto</span>;
  }
  return <span data-testid={`status-inactive-${u.id}`} className="inline-flex items-center gap-1.5 text-xs text-amber-700"><ShieldOff className="h-3.5 w-3.5" /> Disattivato</span>;
};

export default function Users() {
  const { user: currentUser } = useAuth();
  const canDeleteAdmins = ["super_admin", "admin"].includes(currentUser?.role);
  const [tab, setTab] = useState("staff");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", role: "collaboratore", password: "", master_email: "", mobile: "", collab_commission_share_percent: "", collab_extra_share_percent: "" });
  const [permsEditing, setPermsEditing] = useState(null);
  const [permsForm, setPermsForm] = useState({});
  const [activatingId, setActivatingId] = useState(null);
  const [actExpires, setActExpires] = useState("");
  // OTP per modifica utente admin
  const [adminOtpRequested, setAdminOtpRequested] = useState(false);
  const [adminOtpEmail, setAdminOtpEmail] = useState("");
  const [adminOtpCode, setAdminOtpCode] = useState("");
  const [adminOtpBusy, setAdminOtpBusy] = useState(false);

  // 🆕 Quota utenti dell'agenzia (mostra usati / limite piano)
  const [quota, setQuota] = useState({ used: 0, limit: null, plan_name: null, unlimited: true });
  const loadQuota = useCallback(async () => {
    try {
      const { data } = await api.get("/users/quota");
      setQuota(data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadQuota(); }, [loadQuota]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users", { params: { role: tab } });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/users", form); toast.success("Utente creato. Ora attivalo e definisci i permessi."); setOpen(false); setForm(empty); load(); loadQuota(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      name: u.name || "",
      email: u.email || "",
      role: u.role,
      password: "",
      master_email: u.master_email || "",
      collab_commission_share_percent: u.collab_commission_share_percent != null ? String(u.collab_commission_share_percent) : "",
      collab_extra_share_percent: u.collab_extra_share_percent != null ? String(u.collab_extra_share_percent) : "",
      mobile: u.mobile || "",
    });
    setAdminOtpRequested(false);
    setAdminOtpCode("");
    setAdminOtpEmail("");
  };

  const requestAdminEditOtp = async () => {
    if (!editing) return;
    setAdminOtpBusy(true);
    try {
      const { data } = await api.post(`/users/${editing.id}/request-edit-otp`);
      setAdminOtpRequested(true);
      setAdminOtpEmail(data.email || "");
      setAdminOtpCode("");
      if (data.ok) toast.success(`Codice OTP inviato a ${data.email}`);
      else toast.error("Invio OTP fallito (verifica SMTP)");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdminOtpBusy(false);
    }
  };

  const saveEdit = async () => {
    try {
      const payload = {};
      if (editForm.name !== editing.name) payload.name = editForm.name;
      if (editForm.email !== editing.email) payload.email = editForm.email;
      if (editForm.role !== editing.role) payload.role = editForm.role;
      if (editForm.password) payload.password = editForm.password;
      if ((editForm.master_email || "") !== (editing.master_email || "")) payload.master_email = editForm.master_email;
      // Compensi collaboratore (numeric, accettati anche se string vuota → null lato BE)
      const curCSP = editing.collab_commission_share_percent != null ? String(editing.collab_commission_share_percent) : "";
      const curESP = editing.collab_extra_share_percent != null ? String(editing.collab_extra_share_percent) : "";
      if ((editForm.collab_commission_share_percent || "") !== curCSP) {
        payload.collab_commission_share_percent = editForm.collab_commission_share_percent === "" ? 0 : parseFloat(editForm.collab_commission_share_percent);
      }
      if ((editForm.collab_extra_share_percent || "") !== curESP) {
        payload.collab_extra_share_percent = editForm.collab_extra_share_percent === "" ? 0 : parseFloat(editForm.collab_extra_share_percent);
      }
      if ((editForm.mobile || "") !== (editing.mobile || "")) payload.mobile = editForm.mobile;
      if (Object.keys(payload).length === 0) { setEditing(null); return; }
      const isAdminEdit = editing.role === "admin";
      const headers = isAdminEdit ? { "X-Admin-OTP": (adminOtpCode || "").trim() } : {};
      await api.put(`/users/${editing.id}`, payload, { headers });
      toast.success("Utente aggiornato");
      setEditing(null);
      setAdminOtpRequested(false);
      setAdminOtpCode("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openPerms = (u) => {
    setPermsEditing(u);
    const seed = u.permissions || {};
    const next = {};
    PERMISSION_SECTIONS.forEach((s) => { next[s.k] = seed[s.k] || "none"; });
    setPermsForm(next);
  };

  const savePerms = async () => {
    try {
      await api.patch(`/users/${permsEditing.id}/permissions`, { permissions: permsForm });
      toast.success("Permessi aggiornati");
      setPermsEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const forceReset = async (u) => {
    if (!window.confirm(`Inviare email di reset password a ${u.email}?`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/force-reset`);
      toast.success(`Email di reset inviata a ${data.email}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // 🆕 Feb 2026 — SuperAdmin: cambia l'email di login di un utente + master_email + SMTP agenzia
  const changeUserEmail = async (u) => {
    const newEmail = window.prompt(
      `Nuova email di login per ${u.name || u.email}?\n\n` +
      `Attuale: ${u.email}\n\n` +
      `Verrà aggiornata anche master_email e, se questo utente ha un'agenzia collegata, i campi corrispondenti in Impostazioni Email (IMAP/SMTP).\n\n` +
      `⚠ Tutte le sessioni attive dell'utente saranno chiuse.`,
      u.email
    );
    if (!newEmail || newEmail.trim().toLowerCase() === (u.email || "").trim().toLowerCase()) return;
    try {
      const { data } = await api.post(`/superadmin/users/${u.id}/change-email`, {
        new_email: newEmail.trim(),
        also_update_agency_smtp: true,
      });
      const fields = (data.updated_inbox_fields || []).join(", ");
      toast.success(
        `Email cambiata: ${data.old_email} → ${data.new_email}` +
        (fields ? ` · SMTP/IMAP aggiornati (${fields})` : "")
      );
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openActivation = (u) => {
    setActivatingId(u.id);
    setActExpires(u.expires_at ? u.expires_at.slice(0, 16) : "");
  };

  const toggleEmailNotify = async (u, enabled) => {
    try {
      await api.patch(`/users/${u.id}/email-notifications`, { enabled });
      setItems((arr) => arr.map((x) => (x.id === u.id ? { ...x, email_notifications: enabled } : x)));
      toast.success(enabled ? `Notifiche email attivate per ${u.name}` : `Notifiche email disattivate per ${u.name}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveActivation = async (id, isActive) => {
    try {
      await api.patch(`/users/${id}/activation`, { is_active: isActive, expires_at: actExpires || null });
      toast.success(isActive ? "Account attivato" : "Account disattivato");
      setActivatingId(null); setActExpires(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // === Eliminazione utente con OTP (riservata admin) ===
  const [deleteTarget, setDeleteTarget] = useState(null); // user object
  const [deleteStep, setDeleteStep] = useState("idle"); // "idle" | "otp-sent"
  const [deleteOtp, setDeleteOtp] = useState("");
  const [deleteSending, setDeleteSending] = useState(false);
  const [deleteOtpEmail, setDeleteOtpEmail] = useState("");

  const openDeleteDialog = (u) => {
    setDeleteTarget(u);
    setDeleteStep("idle");
    setDeleteOtp("");
    setDeleteOtpEmail("");
  };

  const requestDeleteOtp = async () => {
    if (!deleteTarget) return;
    setDeleteSending(true);
    try {
      const { data } = await api.post(`/users/${deleteTarget.id}/request-delete-otp`);
      setDeleteOtpEmail(data.sent_to || "");
      setDeleteStep("otp-sent");
      toast.success(`Codice inviato a ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setDeleteSending(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (!/^\d{6}$/.test(deleteOtp.trim())) {
      toast.error("Inserisci un codice a 6 cifre");
      return;
    }
    setDeleteSending(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`, { params: { otp: deleteOtp.trim() } });
      toast.success(`Utente ${deleteTarget.surname || ""} ${deleteTarget.name || deleteTarget.email} eliminato`);
      setDeleteTarget(null);
      setDeleteStep("idle");
      setDeleteOtp("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setDeleteSending(false); }
  };

  const stats = useMemo(() => ({
    total: items.length,
    active: items.filter((u) => u.role === "admin" || (u.is_active !== false && !isExpired(u.expires_at))).length,
  }), [items]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="users-page">
      <header className="flex items-center justify-between flex-wrap gap-4 print:hidden">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Amministrazione</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3"><ShieldCheck className="h-8 w-8 sm:h-10 sm:w-10 text-slate-700 bg-slate-200 rounded-lg p-1.5" />Utenti</h1>
          <p className="text-slate-500 mt-1">{stats.total} {tab === "staff" ? "membri staff" : tab === "cliente" ? "clienti portale" : "broker portale"} · {stats.active} attivi</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {/* 🆕 Badge quota utenti — visibile solo se c'è un limite di piano */}
          {!quota.unlimited && quota.limit !== null && (
            <div
              className={`px-3 py-1.5 rounded-lg border text-xs font-semibold flex items-center gap-2 ${quota.used >= quota.limit ? "bg-rose-50 border-rose-300 text-rose-800" : quota.used >= quota.limit * 0.8 ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-emerald-50 border-emerald-300 text-emerald-800"}`}
              data-testid="users-quota-badge"
              title={`Piano ${quota.plan_name || ""}: fino a ${quota.limit} utenti`}
            >
              <span className="text-[10px] uppercase tracking-wider opacity-70">Utenti piano</span>
              <span className="tabular-nums">{quota.used}/{quota.limit}</span>
            </div>
          )}
          <Button variant="outline" data-testid="print-users" onClick={() => window.print()}><Printer className="h-4 w-4 mr-2" /> Stampa</Button>
          {tab === "staff" && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-user-btn"
                  disabled={!quota.unlimited && quota.limit !== null && quota.used >= quota.limit}
                  className="bg-[#0F172A] hover:bg-[#1E293B] disabled:opacity-40 disabled:cursor-not-allowed"
                  title={!quota.unlimited && quota.limit !== null && quota.used >= quota.limit ? `Limite del piano raggiunto (${quota.limit} utenti). Fai upgrade del piano per aggiungerne altri.` : "Crea un nuovo utente staff"}
                >
                  <Plus className="h-4 w-4 mr-2" /> Nuovo utente
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuovo utente staff</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Nome completo</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>Email</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div><Label>Password</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
                  <div>
                    <Label>Ruolo</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                    </Select>
                    {(form.role === "collaboratore" || form.role === "backoffice") && (
                      <p className="text-xs text-amber-700 mt-1.5 bg-amber-50 border border-amber-200 rounded p-2">
                        ⚠️ Vede solo i propri dati (isolamento). Account disabilitato di default — abilitalo poi e definisci i permessi.
                      </p>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                  <Button data-testid="save-user-btn" disabled={!form.email || !form.password || !form.name} onClick={submit} className="bg-[#0F172A] hover:bg-[#1E293B]">Crea</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200 print:hidden" data-testid="users-tabs">
        {TABS.map((t) => (
          <button
            key={t.v}
            data-testid={`tab-${t.v}`}
            onClick={() => setTab(t.v)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.v ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-500 -mt-3 print:hidden">{TABS.find((t) => t.v === tab)?.desc}</p>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-700 text-white text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-6 py-3">Nome</th>
              <th className="text-left px-6 py-3">Email</th>
              <th className="text-left px-6 py-3">Ruolo</th>
              <th className="text-left px-6 py-3">Stato</th>
              <th className="text-left px-6 py-3">Scadenza</th>
              <th className="text-left px-6 py-3">Creato</th>
              {tab === "staff" && <th className="text-left px-4 py-3" title="Riceve le email di notifica automatica">Notifiche email</th>}
              <th className="text-right px-6 py-3 print:hidden">Azioni</th>
            </tr>
          </thead>
          <tbody data-testid="users-table">
            {items.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-slate-400">Nessun utente in questa sezione.</td></tr>}
            {items.map((u) => {
              const expired = isExpired(u.expires_at);
              const active = u.role === "admin" || (u.is_active !== false && !expired);
              return (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                  <td className="px-6 py-4 font-medium">
                    <span className="inline-flex items-center gap-2">
                      <PresenceDot lastSeenAt={u.last_seen_at} />
                      {u.name}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-600 text-xs">{u.email}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${ROLE_COLOR[u.role] || "bg-slate-100 text-slate-700"}`}>
                      {ROLES.find((r) => r.v === u.role)?.l || u.role}
                    </span>
                  </td>
                  <td className="px-6 py-4"><StatusCell u={u} expired={expired} active={active} /></td>
                  <td className="px-6 py-4 text-slate-600 text-xs tabular-nums">
                    {u.role === "admin" && "—"}
                    {u.role !== "admin" && activatingId === u.id && (
                      <Input data-testid={`expires-input-${u.id}`} type="datetime-local" value={actExpires} onChange={(e) => setActExpires(e.target.value)} className="text-xs h-8" />
                    )}
                    {u.role !== "admin" && activatingId !== u.id && (
                      <span data-testid={`expires-display-${u.id}`} className={expired ? "text-red-600 font-medium" : ""}>{formatExpiry(u.expires_at)}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-slate-600 tabular-nums text-xs">
                    {formatDate(u.created_at)}
                    {u.last_renewed_at && (
                      <div className="text-[10px] text-emerald-600 mt-0.5" title={`Rinnovato il ${formatDate(u.last_renewed_at)}`}>
                        🔄 rinnov. {formatDate(u.last_renewed_at)}
                      </div>
                    )}
                  </td>
                  {tab === "staff" && (
                    <td className="px-4 py-4">
                      {u.is_temp_access ? (
                        <span className="text-[11px] text-slate-400" data-testid={`email-notify-demo-${u.id}`} title="Gli utenti demo non ricevono mai notifiche email">Mai (demo)</span>
                      ) : (
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <Switch checked={u.email_notifications ?? u.role === "admin"} onCheckedChange={(v) => toggleEmailNotify(u, v)} data-testid={`email-notify-toggle-${u.id}`} />
                          <span className="text-xs text-slate-600">{(u.email_notifications ?? u.role === "admin") ? "Sì" : "No"}</span>
                        </label>
                      )}
                    </td>
                  )}
                  <td className="px-6 py-4 text-right space-x-2 print:hidden">
                    {u.role !== "admin" && activatingId === u.id && (
                      <>
                        <Button data-testid={`save-act-${u.id}`} size="sm" onClick={() => saveActivation(u.id, true)} className="bg-emerald-600 hover:bg-emerald-700 text-white">Attiva</Button>
                        <Button data-testid={`save-deact-${u.id}`} size="sm" variant="outline" onClick={() => saveActivation(u.id, false)}>Disattiva</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setActivatingId(null); setActExpires(""); }}>Annulla</Button>
                      </>
                    )}
                    {activatingId !== u.id && tab === "staff" && (
                      <>
                        {u.role !== "admin" && (
                          <Button data-testid={`perms-user-${u.id}`} size="sm" variant="outline" onClick={() => openPerms(u)} className="border-purple-200 text-purple-700 hover:bg-purple-50" title="Permessi">
                            <Lock className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button data-testid={`force-reset-${u.id}`} size="sm" variant="outline" onClick={() => forceReset(u)} className="border-amber-300 text-amber-700 hover:bg-amber-50" title="Invia email reset password">
                          <Mail className="h-3.5 w-3.5" />
                        </Button>
                        {currentUser?.role === "super_admin" && (
                          <Button data-testid={`change-email-${u.id}`} size="sm" variant="outline" onClick={() => changeUserEmail(u)} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" title="Cambia email di login (SuperAdmin)">
                            <MailPlus className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button data-testid={`edit-user-${u.id}`} size="sm" variant="outline" onClick={() => openEdit(u)} className="border-slate-300 hover:bg-slate-50" title="Modifica dati">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {u.role !== "admin" && (
                          <Button data-testid={`edit-activation-${u.id}`} size="sm" variant="outline" onClick={() => openActivation(u)} className="border-blue-200 text-blue-700 hover:bg-blue-50" title="Gestisci accesso/scadenza">
                            <Calendar className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </>
                    )}
                    {u.role !== "admin" && activatingId !== u.id && tab !== "staff" && (
                      <Button data-testid={`force-reset-${u.id}`} size="sm" variant="outline" onClick={() => forceReset(u)} className="border-amber-300 text-amber-700 hover:bg-amber-50">
                        <Mail className="h-3.5 w-3.5 mr-1.5" /> Reset
                      </Button>
                    )}
                    {u.role !== "admin" && (
                      <button data-testid={`delete-user-${u.id}`} onClick={() => openDeleteDialog(u)} className="text-red-600 hover:text-red-800 ml-2"><Trash2 className="h-3.5 w-3.5 inline" /></button>
                    )}
                    {/* 🆕 v13: consenti eliminazione admin (con OTP + protezione contro self-delete) */}
                    {u.role === "admin" && canDeleteAdmins && u.id !== currentUser?.id && (
                      <button
                        data-testid={`delete-user-${u.id}`}
                        onClick={() => openDeleteDialog(u)}
                        className="text-red-600 hover:text-red-800 ml-2"
                        title="Elimina questo admin (richiede OTP)"
                      >
                        <Trash2 className="h-3.5 w-3.5 inline" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Permissions matrix dialog */}
      <Dialog open={!!permsEditing} onOpenChange={(o) => !o && setPermsEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-purple-600" /> Permessi · {permsEditing?.name}</DialogTitle>
          </DialogHeader>
          {permsEditing && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
                Imposta per ogni sezione: <strong>Nessuno</strong> (sezione invisibile), <strong>Lettura</strong> (visualizza ma non modifica) o <strong>Lettura+Scrittura</strong> (CRUD completo).
                {(permsEditing.role === "backoffice" || permsEditing.role === "collaboratore") && (
                  <> L&apos;utente vede comunque solo i propri dati (isolamento attivo).</>
                )}
              </p>
              <div className="space-y-2">
                {PERMISSION_SECTIONS.map((s) => (
                  <div key={s.k} className="flex items-center gap-3 p-2 border border-slate-100 rounded">
                    <span className="flex-1 text-sm font-medium">{s.l}</span>
                    <Select value={permsForm[s.k] || "none"} onValueChange={(v) => setPermsForm({ ...permsForm, [s.k]: v })}>
                      <SelectTrigger data-testid={`perm-${s.k}`} className="w-44"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(LEVEL_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermsEditing(null)}>Annulla</Button>
            <Button data-testid="save-perms-btn" onClick={savePerms} className="bg-purple-600 hover:bg-purple-700 text-white">Salva permessi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user data dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-blue-600" /> Modifica utente</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Nome completo</Label><Input data-testid="edit-user-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
              <div>
                <Label>Email</Label>
                <Input data-testid="edit-user-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                {(editForm.email || "").endsWith("@assicura.local") && (
                  <p className="text-[11px] text-amber-700 mt-1">⚠️ Email interna del portale broker/cliente — sconsigliato modificarla.</p>
                )}
              </div>
              {(editing.role === "admin" || editing.role === "backoffice" || editing.role === "agente" || editing.role === "collaboratore") && (
                <>
                  <div>
                    <Label className="flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5 text-purple-600" /> Email master (per OTP e notifiche di sicurezza)
                    </Label>
                    <Input
                      data-testid="edit-user-master-email"
                      type="email"
                      placeholder={editing.role === "admin" ? "es. admin@assicura.it (lasciato vuoto userà l'email di login)" : "facoltativa"}
                      value={editForm.master_email}
                      onChange={(e) => setEditForm({ ...editForm, master_email: e.target.value })}
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      {editing.role === "admin"
                        ? "Se valorizzata, i codici OTP per le operazioni critiche (es. cancellazione massiva polizze) verranno inviati qui invece che all'email di login."
                        : "Email alternativa per comunicazioni di servizio."}
                    </p>
                  </div>
                  <div>
                    <Label>Cellulare</Label>
                    <Input
                      data-testid="edit-user-mobile"
                      type="tel"
                      placeholder="es. +39 333 1234567"
                      value={editForm.mobile}
                      onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                    />
                    <p className="text-[11px] text-slate-500 mt-1">Pronto per future estensioni SMS (OTP / promemoria).</p>
                  </div>
                  {(editing.role === "collaboratore" || editing.role === "agente") && (
                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 bg-emerald-50 border border-emerald-200 rounded-md p-3">
                      <div className="md:col-span-2">
                        <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">💰 Compensi collaboratore</p>
                        <p className="text-[11px] text-slate-600 mt-0.5">Percentuali applicate sulle provvigioni e sulle commissioni extra di ogni polizza assegnata a questo utente.</p>
                      </div>
                      <div>
                        <Label>% Provvigione al collaboratore</Label>
                        <Input
                          data-testid="edit-user-collab-commission"
                          type="number" step="0.1" min="0" max="100"
                          placeholder="0"
                          value={editForm.collab_commission_share_percent}
                          onChange={(e) => setEditForm({ ...editForm, collab_commission_share_percent: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>% Extra fees al collaboratore</Label>
                        <Input
                          data-testid="edit-user-collab-extra"
                          type="number" step="0.1" min="0" max="100"
                          placeholder="0"
                          value={editForm.collab_extra_share_percent}
                          onChange={(e) => setEditForm({ ...editForm, collab_extra_share_percent: e.target.value })}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              <div>
                <Label>Ruolo</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })} disabled={["admin", "cliente", "broker"].includes(editing.role)}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                </Select>
                {editing.role === "admin" && <p className="text-[11px] text-slate-500 mt-1">Ruolo admin non modificabile.</p>}
                {["cliente", "broker"].includes(editing.role) && <p className="text-[11px] text-slate-500 mt-1">Utenti portale: ruolo bloccato.</p>}
              </div>
              <div>
                <Label>Nuova password (facoltativa)</Label>
                <Input data-testid="edit-user-password" type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Lascia vuoto per non cambiarla" />
                <p className="text-[11px] text-slate-500 mt-1">Min 6 caratteri. In alternativa, usa il pulsante &quot;Invia email reset&quot; per far scegliere all&apos;utente.</p>
              </div>
              {editing.role === "admin" && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2">
                  <div className="flex items-center gap-2 text-amber-900 text-sm font-semibold">
                    <ShieldCheck className="h-4 w-4" /> Conferma con OTP (obbligatorio per account admin)
                  </div>
                  {!adminOtpRequested ? (
                    <Button
                      data-testid="admin-edit-otp-request"
                      size="sm"
                      variant="outline"
                      disabled={adminOtpBusy}
                      onClick={requestAdminEditOtp}
                      className="border-amber-400 bg-white"
                    >
                      <Mail className="h-3.5 w-3.5 mr-1.5" />
                      {adminOtpBusy ? "Invio in corso…" : "Invia codice OTP via email"}
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-amber-800">
                        Codice inviato a <strong>{adminOtpEmail}</strong>. Inseriscilo per confermare le modifiche (scade in 5 minuti).
                      </p>
                      <Input
                        data-testid="admin-edit-otp-input"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        placeholder="6 cifre"
                        value={adminOtpCode}
                        onChange={(e) => setAdminOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        className="text-center text-lg font-mono tracking-[0.5em]"
                      />
                      <button
                        type="button"
                        onClick={requestAdminEditOtp}
                        disabled={adminOtpBusy}
                        className="text-xs text-blue-600 hover:underline"
                        data-testid="admin-edit-otp-resend"
                      >
                        Rigenera codice
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            <Button
              data-testid="save-edit-user-btn"
              onClick={saveEdit}
              disabled={
                !editForm.name || !editForm.email ||
                (editing?.role === "admin" && (!adminOtpRequested || adminOtpCode.length < 4))
              }
              className="bg-[#0F172A] hover:bg-[#1E293B]"
            >
              {editing?.role === "admin" ? "Salva con OTP" : "Salva modifiche"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog eliminazione utente con OTP */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteStep("idle"); setDeleteOtp(""); } }}>
        <DialogContent className="w-[95vw] max-w-md" data-testid="delete-user-otp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-rose-600" /> Elimina utente
            </DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-rose-50 border border-rose-200 p-3">
                <p className="text-xs text-rose-800">
                  Stai per eliminare definitivamente:
                </p>
                <p className="font-semibold text-slate-800 mt-1">
                  {(deleteTarget.surname || "") + " " + (deleteTarget.name || "")} <span className="text-slate-500 font-normal">({deleteTarget.email})</span>
                </p>
                <p className="text-[11px] text-rose-700 mt-1">Ruolo: <strong>{deleteTarget.role}</strong></p>
              </div>

              {deleteStep === "idle" ? (
                <>
                  <p className="text-xs text-slate-600">
                    Per sicurezza, l&apos;eliminazione richiede un <strong>codice OTP a 6 cifre</strong> che verrà inviato alla tua email di amministratore.
                  </p>
                  <p className="text-[11px] text-slate-500 italic">
                    Il codice avrà validità di 10 minuti.
                  </p>
                </>
              ) : (
                <>
                  <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2.5 text-xs text-emerald-800">
                    📧 Codice OTP inviato a <strong>{deleteOtpEmail}</strong>. Controlla la tua casella.
                  </div>
                  <div>
                    <Label className="text-xs">Codice OTP (6 cifre) <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="delete-otp-input"
                      value={deleteOtp}
                      onChange={(e) => setDeleteOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      className="font-mono text-center text-2xl tracking-[12px] py-3"
                      autoFocus
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      Hai a disposizione 5 tentativi prima che il codice venga invalidato.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={requestDeleteOtp}
                    disabled={deleteSending}
                    className="text-[11px] text-blue-600 hover:text-blue-800 underline"
                  >
                    Non hai ricevuto il codice? Invia di nuovo
                  </button>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleteSending}>
              Annulla
            </Button>
            {deleteStep === "idle" ? (
              <Button
                data-testid="send-delete-otp-btn"
                onClick={requestDeleteOtp}
                disabled={deleteSending}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Mail className="h-4 w-4 mr-2" />
                {deleteSending ? "Invio…" : "Invia codice OTP"}
              </Button>
            ) : (
              <Button
                data-testid="confirm-delete-user-btn"
                onClick={confirmDelete}
                disabled={deleteSending || !/^\d{6}$/.test(deleteOtp.trim())}
                className="bg-rose-600 hover:bg-rose-700 text-white"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {deleteSending ? "Eliminazione…" : "Elimina definitivamente"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
